import React from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import LayoutHelper from 'infrastructure/LayoutHelper';
import { withFormik, FormikProps } from 'formik';
import { TextField, Paper, Button, FormControl, InputLabel, FormHelperText, withStyles } from '@material-ui/core';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { BookingTermsAndConditions } from './BookingTermsAndConditions';
import { Recaptcha } from '../../components/Shared/Recaptcha';
import MuiPhoneInput from '../../components/Form/MuiPhoneInput';
import UserHelper from 'infrastructure/UserHelper';
import { StateProps, User } from '../../types';

// Form values interface
interface FormValues {
  phone: string;
  clientName?: string;
  clientEmail?: string;
  clientNote?: string;
  tcAccepted?: boolean;
  canSubmit?: boolean;
  pendingClient?: any;
  studioClient?: any;
}

// Props interfaces
interface BookingFormFieldProps {
  label: string;
  name: string;
  children: React.ReactNode;
}

interface OwnProps {
  translate: (key: string) => string;
  classes: {
    root: string;
    buttons: string;
    field: string;
    label: string;
  };
  onSubmit: (values: FormValues) => Promise<void>;
  onSave: () => void;
  buttonTitle: string;
  owner: User;
}

// Class interface - using "I" prefix as requested
interface IBookingFormNoPin {
  BookingFormField: React.FC<BookingFormFieldProps>;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  renderTC: () => JSX.Element;
}

class BookingFormNoPin extends React.Component<BookingFormNoPin> implements IBookingFormNoPin {
  constructor(props: BookingFormNoPin) {
    super(props);
    this.state = {};
  }

  BookingFormField: React.FC<BookingFormFieldProps> = ({ label, name, children }) => {
    const { classes, errors } = this.props;
    const error = errors[name];
    return (
      <FormControl className={classes.field} error={!!error}>
        <InputLabel className={classes.label}>
          {label}
          {error && <FormHelperText>&nbsp;{error}</FormHelperText>}
        </InputLabel>
        {children}
      </FormControl>
    );
  };

  render() {
    const { BookingFormField } = this;
    const { classes, values, isSubmitting, handleChange, handleSubmit, dirty, domain } = this.props;

    return (
      <Paper className={classes.root} elevation={1}>
        <form onSubmit={handleSubmit}>
          <BookingFormField label={this.props.translate('manageRegistration.clientPhone')} name="phone">
            <MuiPhoneInput
              defaultCountry={domain.domain.countryCode}
              value={values.phone}
              id="phone"
              autoFocus
              onChange={(phone: string) => {
                handleChange('phone')(phone);
                /* Commented code remains commented as in original */
                /*handleChange("studioClient")(null);*/
                /*handleChange("pendingClient")(null);*/
                /*handleChange("clientEmail")(null);*/
                /*handleChange("clientName")(null);*/
                /*handleChange("canSubmit")(null);*/
                /*handleChange("tcAccepted")(null);*/
                //handleChange("pin")(null);
                //this.setState({pinValid: false});
                //return this.getClient(phone)
              }}
            />
          </BookingFormField>

          <BookingFormField label={this.props.translate('manageRegistration.clientName')} name="clientName">
            <TextField id="clientName" value={values.clientName} onChange={handleChange} margin="normal" />
          </BookingFormField>

          <BookingFormField label={this.props.translate('manageRegistration.clientEmail')} name="clientEmail">
            <TextField
              id="clientEmail"
              value={values.clientEmail}
              onChange={this.handleEmailChange}
              margin="clientEmail"
            />
          </BookingFormField>
          {/*<BookingFormField label={this.props.translate("manageRegistration.clientNote")}*/}
          {/*                  name="clientNote">*/}
          {/*    <TextField*/}
          {/*        id="clientNote"*/}
          {/*        multiline*/}
          {/*        value={values.clientNote}*/}
          {/*        onChange={handleChange}*/}
          {/*        margin="clientNote"*/}
          {/*    />*/}
          {/*</BookingFormField>*/}
          {this.renderTC()}
          <BookingFormField name="canSubmit" label={this.props.translate('common.captcha')}>
            <div style={{ marginBottom: '10px' }}>
              <Recaptcha onVerify={(canSubmit: boolean) => this.props.handleChange('canSubmit')(canSubmit)} />
            </div>
          </BookingFormField>

          <div className={classes.buttons}>
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting || !dirty}>
              {isSubmitting && LayoutHelper.renderLoader()}
              {this.props.buttonTitle}
            </Button>
          </div>
        </form>
      </Paper>
    );
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value) {
      value = value.trim();
    }
    this.props.handleChange('clientEmail')(value);
  };

  renderTC = () => {
    const { BookingFormField } = this;
    const { values, handleChange, owner } = this.props;

    return (
      <BookingFormField label={this.props.translate('common.termsAndConditions')} name="tcAccepted">
        <BookingTermsAndConditions
          accepted={values.tcAccepted}
          onChange={(a: boolean) => {
            handleChange('tcAccepted')(a);
          }}
          owner={owner}
        />
      </BookingFormField>
    );
  };
}

function mapStateToProps(state: any): StateProps {
  let translate = getTranslate(state.locale);
  let user = state.user;
  let myStudios = state.myStudios;
  let domain = state.domain;
  return { translate, user, myStudios, domain };
}

export default withStyles((theme) => ({
  root: {
    marginBottom: `0 auto`,
    padding: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    margin: `${theme.spacing(2)}px 0`,
  },
  label: {
    marginBottom: theme.spacing(1),
    position: 'relative',
    transform: 'none',
    display: 'flex',
    alignItems: 'center',
  },
}))(
  connect(mapStateToProps)(
    withFormik<OwnProps & StateProps, FormValues>({
      mapPropsToValues: (props) => {
        let phone = '';
        if (UserHelper.isLoggedIn(props.user)) {
          if (props.user.phone) {
            phone = props.user.phone;
          }
        } else {
          const storedPhone = localStorage.getItem('validPhone');
          if (storedPhone) {
            phone = storedPhone;
          }
        }

        return {
          phone: phone,
        };
      },
      validate: (values, props) => {
        const errors: { [key: string]: string } = {};
        const mandatoryFields = ['clientName', 'phone'];
        if (values.pendingClient) {
          mandatoryFields.push('tcAccepted');
          mandatoryFields.push('canSubmit');
        }
        mandatoryFields.forEach((field) => {
          if (!values[field as keyof FormValues]) {
            errors[field] = '*';
          }
        });

        if (values.clientEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i.test(values.clientEmail)) {
          errors.clientEmail = props.translate('common.invalidEmail');
        }
        if (values.phone && !isValidPhoneNumber(values.phone)) {
          errors.phone = props.translate('common.invalidPhone');
        }

        return errors;
      },
      handleReset: (props) => {
        props.onSave();
      },
      handleSubmit: async (values, { setSubmitting, resetForm, props }) => {
        setSubmitting(true);
        await props.onSubmit(values);
        setSubmitting(false);
      },
      displayName: 'BookingFormNoPin',
    })(BookingFormNoPin)
  )
);
